import React, { useEffect, useState } from "react";
import { Exercise, User, SharedWorkoutRoutine } from "../../api/types.ts";
import { SharedRoutineCard } from "./SharedRoutineCard.tsx";
import { useSharedExercises } from "../../api/hooks.ts";
import { Banner } from "../widgets/Banner.tsx";
import { FetchedExercise } from "../../util/exercise.tsx";

interface SharedRoutineListProps {
  friends: Partial<User>[] | undefined;
  isQueried: boolean;
  untilRoutinesLimit: number;
  untilExercisesLimit: number;
  routines: SharedWorkoutRoutine[];
  exercises: Exercise[];
  goToRoutines: () => void;
}

const SharedRoutineList: React.FC<SharedRoutineListProps> = ({
  friends,
  isQueried,
  untilRoutinesLimit,
  untilExercisesLimit,
  routines,
  exercises,
  goToRoutines,
}) => {
  const sharedExercisesMutation = useSharedExercises();

  const [sharedExercises, setSharedExercises] = useState<
    Map<string, Exercise | undefined>
  >(new Map());

  useEffect(() => {
    const sharedExercises = routines
      .flatMap((routine) =>
        routine.exercises.filter(
          (ex) => !exercises.find((existing) => existing.id === ex.exercise_id),
        ),
      )
      .map((ex) => ex.exercise_id);

    async function fetchExercises(exerciseIds: string[]) {
      sharedExercisesMutation.mutate(exerciseIds, {
        onSuccess: (fetched) => {
          setSharedExercises((prev) => {
            const map = new Map();
            fetched.forEach((ex) => map.set(ex.id, ex));
            return new Map([...Array.from(prev), ...Array.from(map)]);
          });
        },
      });
    }

    fetchExercises(sharedExercises);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercises, routines]);

  // Create a map for quick lookup
  const exercisesMap = React.useMemo(() => {
    const map = new Map<string, Map<string, FetchedExercise>>();
    routines?.forEach((routine) => {
      const routineExerciseMap = new Map<string, FetchedExercise>();
      // Add exercises referenced in the shared routine
      routine.exercises.forEach(async (ex) => {
        const exercise = exercises.find(
          (existing) => existing.id === ex.exercise_id,
        );
        routineExerciseMap.set(ex.exercise_id, {
          exercise: exercise ?? sharedExercises.get(ex.exercise_id),
          external: !exercise,
        });
      });
      // Add existing exercises so user can access them when editing the routine exercises
      exercises.forEach((ex) => {
        routineExerciseMap.set(ex.id, { exercise: ex, external: false });
      });

      map.set(routine.id, routineExerciseMap);
    });
    return map;
  }, [routines, exercises, sharedExercises]);

  return isQueried && routines.length === 0 ? (
    <p className="text-gray-500 mb-4">Found no matching routines.</p>
  ) : routines.length === 0 ? (
    <p className="text-gray-500 mb-4">No routine has been shared with you.</p>
  ) : (
    <div className="flex flex-col gap-6 py-4">
      {untilRoutinesLimit < 2 &&
        (untilRoutinesLimit < 1 ? (
          <Banner
            visible
            variant="error"
            title="Routine limit reached"
            body="You will need to delete one of your existing routines before importing one."
          />
        ) : (
          <Banner
            visible
            variant="warning"
            title="Approaching routine limit"
            body={`You have ${untilRoutinesLimit} routine${untilRoutinesLimit > 1 ? "s" : ""} left to import or create.`}
          />
        ))}
      {untilExercisesLimit < 2 &&
        (untilExercisesLimit < 1 ? (
          <Banner
            visible
            variant="error"
            title="Exercise limit reached"
            body="You may need to edit the exercises of a shared routine before importing it."
          />
        ) : (
          <Banner
            visible
            variant="warning"
            title="Approaching exercise limit"
            body={`You have ${untilExercisesLimit} exercise${untilExercisesLimit > 1 ? "s" : ""} left to import or create. You may need to edit the exercises of a shared routine before importing it.`}
          />
        ))}

      {routines.map((routine) => (
        <div key={routine.id}>
          <SharedRoutineCard
            routine={routine}
            availableExercises={exercisesMap.get(routine.id) ?? new Map()}
            friends={friends}
            untilRoutinesLimit={untilRoutinesLimit}
            untilExercisesLimit={untilExercisesLimit}
            goToRoutines={goToRoutines}
          />
        </div>
      ))}
    </div>
  );
};

export { SharedRoutineList };
